import { useSubdomainInfo } from 'shared/Site/api'
import { useGetObjectTranslation } from 'utils/useGetObjectTranslation'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Password from '@mui/icons-material/Password'
import PersonPin from '@mui/icons-material/PersonPin'
import Box from '@mui/material/Box'
import { Profiles } from 'components/ArteelIcons/Profiles'
import { HeaderPage } from 'components/HeaderPage/HeaderPage'
import { FieldsExtended } from 'components/JSONForm/types'
import { Stepper } from 'components/Stepper/Stepper'
import { FieldNames } from 'pages/Login/api/useGetActivationCodeForm'
import { ChooseMethod } from 'pages/Login/RegisterAccount/ChooseMethod'
import { EnterCode } from 'pages/Login/RegisterAccount/EnterCode'
import { EnterEmail } from 'pages/Login/RegisterAccount/EnterEmail'
import { EnterPersonalDetails } from 'pages/Login/RegisterAccount/EnterPersonalDetails'
import { RegisterMethod, registerMethods } from 'pages/Login/RegisterAccount/types'
import { getResponsiveSize } from 'theme/styles.utils'

export const RegisterAccount = () => {
  const { data: subdomain } = useSubdomainInfo()
  const [step, setStep] = useState(0)
  const [loadedSteps, setLoadedSteps] = useState<number[]>([])
  const [method, setMethod] = useState<RegisterMethod | null>(null)
  const [code, setCode] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [fields, setFields] = useState<FieldsExtended<FieldNames>>(null)
  const [personalIdHint, setPersonalIdHint] = useState<
    { translation: Record<string, { content: string }> } | null | undefined
  >(null)
  const { t } = useTranslation()
  const navigate = useNavigate()

  const onlyOneMethodAvailable = useMemo(() => {
    return (
      (subdomain?.data?.subscriber?.has_registration_codes && !subdomain?.data?.subscriber?.has_registration_email) ||
      (!subdomain?.data?.subscriber?.has_registration_codes && subdomain?.data?.subscriber?.has_registration_email)
    )
  }, [subdomain?.data?.subscriber])

  useEffect(() => {
    if (onlyOneMethodAvailable) {
      setMethod(subdomain?.data?.subscriber?.has_registration_codes ? registerMethods.code : registerMethods.domain)
      setStep(1)
      setLoadedSteps([1, 2])
    }
  }, [subdomain?.data?.subscriber])

  const steps = useMemo(
    () => [
      {
        id: 0,
        label: t('auth.register_account.choose_method'),
        icon: <PersonPin />,
        content: (
          <ChooseMethod
            disabled={step !== 0}
            disabledCodes={!subdomain?.data?.subscriber?.has_registration_codes}
            disabledEmail={!subdomain?.data?.subscriber?.has_registration_email}
            method={method}
            onSuccess={(method) => {
              setMethod(method)
              setStep(1)
              setLoadedSteps([1, 2])
            }}
          />
        ),
      },
      {
        id: 1,
        label: t(
          method === registerMethods.domain ? 'auth.activate_account.enter_email' : 'auth.activate_account.enter_code'
        ),
        icon: <Password />,
        lazyLoaded: true,
        content:
          method === registerMethods.domain ? (
            <EnterEmail
              disabled={step !== 1}
              onSuccess={(data) => {
                setStep(2)
                setFields(data?.fields)
                setPersonalIdHint(data?.personal_id_hint || null)
                setEmail(data?.email)
              }}
              onBack={() => {
                setLoadedSteps([])
                setStep(0)
                setFields(null)
                setPersonalIdHint(null)
                setCode(null)
                setEmail(null)
                setMethod(null)
              }}
            />
          ) : (
            <EnterCode
              disabled={step !== 1}
              onSuccess={(data) => {
                setStep(2)
                setCode(data?.code)
                setPersonalIdHint(data?.personal_id_hint || null)
                setFields(data?.fields)
              }}
              onBack={() => {
                setLoadedSteps([])
                setStep(0)
                setFields(null)
                setPersonalIdHint(null)
                setCode(null)
                setEmail(null)
                setMethod(null)
              }}
            />
          ),
      },
      {
        id: 2,
        label: t('auth.activate_account.enter_personal_information'),
        optionalLabel: useGetObjectTranslation(personalIdHint?.translation, 'content'),
        icon: <Profiles />,
        lazyLoaded: true,
        content: (
          <EnterPersonalDetails
            code={code}
            email={email}
            method={method}
            fields={fields}
            onBack={() => {
              setLoadedSteps([])
              setStep(0)
              setEmail(null)
              setCode(null)
              setFields(null)
              setMethod(null)
            }}
            onSuccess={({ email }) => navigate(`/auth/activate-account/confirmation/${email}`)}
          />
        ),
      },
    ],
    [step]
  )

  return (
    <Box
      sx={(theme) => ({
        display: 'grid',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      })}
    >
      <Box>
        <HeaderPage text={t('auth.register_account')} uppercase={false} color="medium" />
        <Box
          sx={(theme) => ({
            margin: 2,
            [theme.breakpoints.up('lg')]: { width: getResponsiveSize(800) },
          })}
        >
          <Stepper activeStep={step} steps={steps} loadedSteps={loadedSteps} />
        </Box>
      </Box>
    </Box>
  )
}
